import { AvatarUpload } from "@/components/AvatarUpload";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { type StatusBarItem, useWorkspacePage } from "@/components/workspace";
import { MemberService } from "@/gen/omnios/v1/member_connect.js";
import { OrganizationService } from "@/gen/omnios/v1/organization_connect.js";
import { formatDate } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useOrganizationStore } from "@/lib/organization";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, CreditCard, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/organization")({
	component: OrganizationSettingsPage,
});

const client = createClient(OrganizationService, transport);
const memberClient = createClient(MemberService, transport);

function OrganizationSettingsPage() {
	const { t } = useTranslation("settings");
	const { t: tc } = useTranslation("common");

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("org_breadcrumb") },
	]);

	const {
		selected,
		fetchOrganizations,
		organizations,
		selectedOrgId,
		isLoading,
	} = useOrganizationStore();
	const storeOrg = selected();
	const org = storeOrg ?? organizations[0] ?? null;

	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [currency, setCurrency] = useState("");
	const [fiscalStart, setFiscalStart] = useState(1);
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	useEffect(() => {
		if (organizations.length === 0 && !isLoading) {
			fetchOrganizations();
		}
	}, []);

	useEffect(() => {
		if (org && !selectedOrgId) {
			useOrganizationStore.getState().setSelectedOrg(org.id);
		}
	}, [org, selectedOrgId]);

	useEffect(() => {
		if (org) {
			setName(org.name);
			setSlug(org.slug);
			setCurrency(org.reportingCurrency);
			setFiscalStart(org.fiscalYearStart);
			setDirty(false);
		}
	}, [org?.id]);

	// Autosave debounce (matching workspace pattern)
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!dirty || !org) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(async () => {
			setSaving(true);
			setSaved(false);
			try {
				await client.updateOrganization({
					id: org.id,
					name,
					slug,
					reportingCurrency: currency,
					fiscalYearStart: fiscalStart,
				});
				await fetchOrganizations();
				setDirty(false);
				setSaved(true);
				setTimeout(() => setSaved(false), 2000);
			} catch {
				// silently retry on next change
			} finally {
				setSaving(false);
			}
		}, 800);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [dirty, name, slug, currency, fiscalStart, org?.id, fetchOrganizations]);

	// Status bar (matching workspace pattern)
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "status",
				content: saving
					? t("general_saving", { defaultValue: "Сохранение..." })
					: saved
						? `✓ ${t("general_saved")}`
						: dirty
							? t("general_unsaved", { defaultValue: "Не сохранено" })
							: "",
				align: "left" as const,
			},
		],
		[saving, saved, dirty, t],
	);
	useWorkspacePage({ statusBar: statusBarItems });

	// Avatar upload placeholder (no backend support yet)
	const handleAvatarUpload = useCallback(async (_file: File) => {
		// TODO: add UploadOrganizationAvatar RPC to proto and backend
	}, []);

	if (!org && isLoading) {
		return (
			<div className="max-w-lg space-y-4 p-6">
				<Skeleton className="h-16 w-16 rounded-full" />
				<Skeleton className="h-5 w-48" />
				<Skeleton className="h-5 w-32" />
			</div>
		);
	}

	if (!org) {
		return (
			<div className="p-6">
				<p className="text-sm text-muted-foreground">{t("org_select_first")}</p>
			</div>
		);
	}

	const initials =
		org.name
			.split(/\s+/)
			.map((w) => w[0])
			.join("")
			.slice(0, 2)
			.toUpperCase() || "?";

	const entityCount = useWorkspaceStore
		.getState()
		.workspaces.filter((w) => w.organizationId === org.id).length;

	const MONTH_NAMES = [
		"", // 0
		t("org_month_jan"),
		t("org_month_feb"),
		t("org_month_mar"),
		t("org_month_apr"),
		t("org_month_may"),
		t("org_month_jun"),
		t("org_month_jul"),
		t("org_month_aug"),
		t("org_month_sep"),
		t("org_month_oct"),
		t("org_month_nov"),
		t("org_month_dec"),
	];

	return (
		<div className="max-w-lg space-y-6 p-6 h-full overflow-auto">
			{/* Avatar + name header (matching workspace pattern) */}
			<div className="flex items-center gap-4">
				<AvatarUpload
					currentUrl=""
					initials={initials}
					size={64}
					onUpload={handleAvatarUpload}
				/>
				<div className="space-y-1">
					<p className="text-base font-medium">{org.name}</p>
					<p className="text-sm text-muted-foreground">
						{org.slug}
						{entityCount > 0 && (
							<span className="ml-2">
								· {entityCount}{" "}
								{entityCount === 1
									? tc("entity")
									: tc("entities").toLowerCase()}
							</span>
						)}
					</p>
				</div>
			</div>

			<Separator />

			{/* Editable fields (inline, matching workspace pattern) */}
			<div className="space-y-4">
				<div className="space-y-1.5">
					<Label>{tc("name")}</Label>
					<Input
						value={name}
						onChange={(e) => {
							setName(e.target.value);
							setDirty(true);
						}}
					/>
				</div>
				<div className="space-y-1.5">
					<Label>Slug</Label>
					<Input
						value={slug}
						onChange={(e) => {
							setSlug(e.target.value);
							setDirty(true);
						}}
					/>
				</div>
				<div className="space-y-1.5">
					<Label>{t("org_reporting_currency")}</Label>
					<Input
						value={currency}
						onChange={(e) => {
							setCurrency(e.target.value);
							setDirty(true);
						}}
						placeholder="RUB"
					/>
				</div>
				<div className="space-y-1.5">
					<Label>{t("org_fiscal_year_start")}</Label>
					<Input
						type="number"
						min={1}
						max={12}
						value={fiscalStart}
						onChange={(e) => {
							setFiscalStart(Number.parseInt(e.target.value, 10) || 1);
							setDirty(true);
						}}
					/>
					<p className="text-xs text-muted-foreground">
						{t("org_fiscal_year_hint")} ·{" "}
						{fiscalStart >= 1 && fiscalStart <= 12
							? MONTH_NAMES[fiscalStart]
							: ""}
					</p>
				</div>
				<div className="h-5">
					{saving && (
						<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							{t("general_saving")}
						</span>
					)}
					{saved && (
						<span className="flex items-center gap-1.5 text-xs text-green-600">
							<Check className="h-3.5 w-3.5" />
							{t("general_saved")}
						</span>
					)}
				</div>
			</div>

			<Separator />

			{/* Info section */}
			<div className="space-y-2">
				<h3 className="text-sm font-medium">
					{t("general_info", { defaultValue: "Информация" })}
				</h3>
				<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
					<span className="text-muted-foreground">ID</span>
					<code className="text-xs">{org.id}</code>
					<span className="text-muted-foreground">Slug</span>
					<span>{org.slug}</span>
				</div>
			</div>

			<Separator />

			{/* Billing */}
			<BillingSummary />
		</div>
	);
}

function BillingSummary() {
	const { t } = useTranslation("settings");

	const { data, isLoading } = useQuery({
		queryKey: ["billing", "my-subscription"],
		queryFn: () => memberClient.getMySubscription({}),
	});

	const sub = data?.subscription;

	return (
		<div className="space-y-2">
			<h3 className="flex items-center gap-1.5 text-sm font-medium">
				<CreditCard className="h-4 w-4" />
				{t("org_billing")}
			</h3>
			{isLoading ? (
				<div className="space-y-2">
					<Skeleton className="h-5 w-48" />
					<Skeleton className="h-5 w-32" />
				</div>
			) : !sub ? (
				<p className="text-sm text-muted-foreground">
					{t("org_no_subscription")}
				</p>
			) : (
				<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
					<span className="text-muted-foreground">{t("org_plan")}</span>
					<span className="flex items-center gap-2">
						{sub.planName}
						<Badge variant={sub.status === "active" ? "default" : "secondary"}>
							{sub.status}
						</Badge>
					</span>
					<span className="text-muted-foreground">{t("org_seats")}</span>
					<span>{sub.seatCount}</span>
					<span className="text-muted-foreground">{t("org_period")}</span>
					<span>{sub.period}</span>
					{sub.currentPeriodEnd && (
						<>
							<span className="text-muted-foreground">{t("org_renewal")}</span>
							<span>{formatDate(sub.currentPeriodEnd.toDate())}</span>
						</>
					)}
					{sub.trialEnd && (
						<>
							<span className="text-muted-foreground">
								{t("org_trial_end")}
							</span>
							<span>{formatDate(sub.trialEnd.toDate())}</span>
						</>
					)}
				</div>
			)}
		</div>
	);
}
